<template>
  <b-modal
    :id="`modal-signature-gate-${uniqueId}`"
    ref="modal-signature-gate"
    title="Assinatura Obrigatória para Relatório"
    size="lg"
    hide-footer
    centered
    no-close-on-backdrop
    header-bg-variant="warning"
    header-class="text-white"
    title-class="text-white"
  >
    <div class="p-1">
      <div class="alert alert-warning py-1 px-2 font-small-3 mb-2">
        <feather-icon
          icon="AlertTriangleIcon"
          class="mr-50"
        />
        Este relatório exige assinatura. Por favor, assine no quadro abaixo antes de prosseguir com a impressão.
      </div>

      <div
        class="signature-canvas-container border rounded bg-light mb-2 d-flex justify-content-center shadow-inner position-relative"
        style="touch-action: none; user-select: none; -webkit-user-select: none; cursor: crosshair;"
      >
        <!-- Guia Visual -->
        <div class="signature-guide-overlay">
          <div class="signature-line-guide" />
          <span class="signature-label-guide">Assine sobre a linha</span>
        </div>

        <vue-drawing-canvas
          ref="signaturePad"
          v-model="signatureImage"
          :width="canvasWidth"
          :height="200"
          stroke-type="dash"
          line-cap="round"
          line-join="round"
          :line-width="3"
          color="#000000"
          background-color="transparent"
          save-as="png"
          :styles="{ border: 'none', 'touch-action': 'none', 'user-select': 'none', '-webkit-user-select': 'none' }"
          @update:image="signatureImage = $event"
        />
      </div>

      <div class="d-flex justify-content-between align-items-center">
        <b-button
          v-ripple.400="'rgba(186, 191, 199, 0.15)'"
          variant="outline-secondary"
          :disabled="isSaving"
          @click="clear"
        >
          <feather-icon
            icon="Trash2Icon"
            class="mr-50"
          />
          Limpar
        </b-button>

        <div>
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="flat-secondary"
            class="mr-1"
            :disabled="isSaving"
            @click="cancel"
          >
            Cancelar
          </b-button>
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            :disabled="isSaving || !signatureImage || signatureImage.length < 200"
            @click="confirmAndSave"
          >
            <b-spinner
              v-if="isSaving"
              small
              class="mr-50"
            />
            <feather-icon
              v-else
              icon="PrinterIcon"
              class="mr-50"
            />
            {{ isSaving ? 'Salvando...' : 'Salvar e Imprimir' }}
          </b-button>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { BModal, BButton, BSpinner } from 'bootstrap-vue'
import VueDrawingCanvas from 'vue-drawing-canvas'
import Ripple from 'vue-ripple-directive'
import mixinsGlobal from '@/mixins'

export default {
  name: 'SignatureGateModal',
  components: {
    BModal,
    BButton,
    BSpinner,
    VueDrawingCanvas,
  },
  directives: {
    Ripple,
  },
  mixins: [mixinsGlobal],
  props: {
    // Dados da entidade para persistência
    entityData: {
      type: Object,
      default: () => ({}),
    },
    storeName: {
      type: String,
      required: true,
    },
    actionName: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      signatureImage: '',
      canvasWidth: 600,
      uniqueId: Math.floor(Math.random() * 1000),
      isSaving: false,
      currentReport: null,
    }
  },
  mounted() {
    this.updateCanvasWidth()
    window.addEventListener('resize', this.updateCanvasWidth)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateCanvasWidth)
  },
  methods: {
    updateCanvasWidth() {
      const width = window.innerWidth
      if (width < 768) {
        this.canvasWidth = width - 80
      } else {
        this.canvasWidth = 600
      }
    },
    /**
     * Aciona o gate para um relatório específico
     */
    trigger(report) {
      this.currentReport = report
      this.signatureImage = ''
      this.$bvModal.show(`modal-signature-gate-${this.uniqueId}`)
      setTimeout(() => {
        if (this.$refs.signaturePad) {
          this.$refs.signaturePad.reset()
        }
      }, 200)
    },
    clear() {
      this.$refs.signaturePad.reset()
      this.signatureImage = ''
    },
    cancel() {
      this.$bvModal.hide(`modal-signature-gate-${this.uniqueId}`)
      this.$emit('cancelled')
    },
    cropSignature() {
      try {
        // Tenta pegar o canvas de forma mais robusta
        const canvas = this.$refs.signaturePad.$refs.canvas || this.$refs.signaturePad.$el.querySelector('canvas') || this.$refs.signaturePad.$el

        if (!canvas || typeof canvas.getContext !== 'function') {
          console.warn('Canvas não encontrado para recorte, usando imagem original')
          return null
        }

        const ctx = canvas.getContext('2d')
        const { width, height } = canvas
        const pix = ctx.getImageData(0, 0, width, height).data
        let minX = width
        let minY = height
        let maxX = 0
        let maxY = 0

        for (let y = 0; y < height; y += 1) {
          for (let x = 0; x < width; x += 1) {
            const index = (y * width + x) * 4
            const alpha = pix[index + 3]
            if (alpha > 0) {
              if (x < minX) minX = x
              if (x > maxX) maxX = x
              if (y < minY) minY = y
              if (y > maxY) maxY = y
            }
          }
        }

        // Adiciona um pequeno padding
        const padding = 5
        minX = Math.max(0, minX - padding)
        minY = Math.max(0, minY - padding)
        maxX = Math.min(width, maxX + padding)
        maxY = Math.min(height, maxY + padding)

        const croppedWidth = maxX - minX
        const croppedHeight = maxY - minY

        if (croppedWidth <= 0 || croppedHeight <= 0) return null

        const croppedCanvas = document.createElement('canvas')
        croppedCanvas.width = croppedWidth
        croppedCanvas.height = croppedHeight
        const croppedCtx = croppedCanvas.getContext('2d')

        croppedCtx.drawImage(canvas, minX, minY, croppedWidth, croppedHeight, 0, 0, croppedWidth, croppedHeight)
        return croppedCanvas.toDataURL('image/png')
      } catch (e) {
        console.error('Erro ao processar recorte de assinatura:', e)
        return null
      }
    },
    confirmAndSave() {
      if (!this.signatureImage) return

      const croppedImage = this.cropSignature()
      if (croppedImage) {
        this.signatureImage = croppedImage
      }

      this.isSaving = true

      // Lógica de extração de ID baseada nos formulários originais do sistema
      const extractId = val => {
        if (val === null || val === undefined) return null
        if (typeof val === 'object' && val.id !== undefined) return val.id
        return val
      }

      // Constrói o payload para salvar no banco
      // Nota: Começamos com uma cópia rasa e ajustamos os campos problemáticos
      const payload = { ...this.entityData }

      // Injeta a assinatura
      payload.assinaturaDigital = this.signatureImage

      // Limpeza/Mapeamento de campos para DTOs do Spring (PF)
      if (payload.sexo !== undefined) payload.sexo = extractId(payload.sexo)
      if (payload.estadoCivil !== undefined) payload.estadoCivil = extractId(payload.estadoCivil)
      if (payload.profissao !== undefined) payload.profissao = extractId(payload.profissao)
      if (payload.nacionalidade !== undefined) payload.nacionalidade = extractId(payload.nacionalidade)
      if (payload.naturalidade !== undefined) payload.naturalidade = extractId(payload.naturalidade)
      if (payload.tipoRegistro !== undefined) payload.tipoRegistro = extractId(payload.tipoRegistro)
      if (payload.situacaoRegistro !== undefined) payload.situacaoRegistro = extractId(payload.situacaoRegistro)
      if (payload.formaPagamento !== undefined) payload.formaPagamento = extractId(payload.formaPagamento)

      // Formatação de data para o formato aceito pelo backend (YYYY-MM-DD para LocalDate)
      if (payload.dataNascimento) {
        payload.dataNascimento = String(payload.dataNascimento).split('T')[0]
      }

      // Para Instant (dataAdesao), precisamos do formato ISO completo
      if (payload.dataAdesao) {
        const dataStr = String(payload.dataAdesao)
        if (!dataStr.includes('T')) {
          payload.dataAdesao = `${dataStr}T00:00:00Z`
        } else {
          payload.dataAdesao = dataStr
        }
      }

      // Mapeamento específico para PJ (Fornecedores) se necessário
      if (payload.naturezaFiscal !== undefined) {
        payload.naturezaFiscal = extractId(payload.naturezaFiscal)
      }

      this.$store.dispatch(`${this.storeName}/${this.actionName}`, payload)
        .then(response => {
          this.isSaving = false
          if (response.response) {
            this.MensagemError('Falha ao Salvar Assinatura', response.response.data.error || 'Erro ao persistir assinatura antes da impressão.')
            return
          }

          this.showMessageSuccess('Assinatura', 'Assinatura salva com sucesso! Gerando relatório...')

          // Notifica o pai que pode imprimir, enviando o relatório e a assinatura
          this.$emit('confirmed', {
            report: this.currentReport,
            signature: this.signatureImage,
          })

          this.$bvModal.hide(`modal-signature-gate-${this.uniqueId}`)
        })
        .catch(error => {
          this.isSaving = false
          this.MensagemError('Erro', 'Não foi possível salvar a assinatura para imprimir o relatório.')
          console.error(error)
        })
    },
  },
}
</script>

<style lang="scss" scoped>
.signature-canvas-container {
  background-color: #fcfcfc;
  min-height: 200px;
  overflow: hidden;
}

.signature-guide-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  pointer-events: none;
  z-index: 1;
}

.signature-line-guide {
  width: 80%;
  height: 2px;
  background-color: #b9b9c3;
  margin-top: 60px;
}

.signature-label-guide {
  font-size: 0.8rem;
  color: #82868b;
  margin-top: 8px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.shadow-inner {
  box-shadow: inset 0 2px 4px 0 rgba(0, 0, 0, 0.06);
}

::v-deep canvas {
  touch-action: none !important;
  user-select: none !important;
  -webkit-user-select: none !important;
}
</style>
